import React, { useState, useEffect, useCallback } from "react";
import TablePagesTitle from "../../../components/table-pages-title/TablePagesTitle";
import ReportPageAction from "../page-action/ReportPageAction";
import ErrorBoundary from "../../../components/error-boundary";
import ComponentViewFallback from "../../../components/error-boundary/fallback/ComponentViewFallback";
import TotalOwingTable from "../../../components/table/all-tables/reportable/totalOwingZero/TotalOwingTable";
import OwingFilter from "./OwingFilter";
import {
  showToast,
  downloadFileFromResponse,
  formatDateWithDayjs,
  get3MonthsAgo
} from "../../../utils/utils";
import privateAxios from "../../../utils/axiosPrivate";
import { useColumnSort } from "../../../hooks/useColumnSort";
import {
  getTotalOwingNotZeroReportUrl,
  getTotalOwingNotZeroReportDownloadUrl
} from "../../../utils/constants/apiEndPoints";
import {
  PAGE_SIZE_50,
  showToastType,
  YYYYMMDD,
  HTTP_STATUS_SUCCESS,
  ZERO_NUMBER,
  ENTER_KEY
} from "../../../utils/constants/appConstants";

export default function TotalOwingNotZeroReport() {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [totalPages, setTotalPages] = useState(ZERO_NUMBER);
  const [pageIndex, setPageIndex] = useState(ZERO_NUMBER);
  const [pageSize, setPageSize] = useState(PAGE_SIZE_50);

  const [initialStartDate, setInitialStartDate] = useState(get3MonthsAgo());
  const [initialEndDate, setInitialEndDate] = useState(new Date());

  const [selectedStartDate, setSelectedStartDate] = useState(initialStartDate);
  const [selectedEndDate, setSelectedEndDate] = useState(initialEndDate);

  const [searchKeyword, setSearchKeyword] = useState("");
  const [appliedSearchKeyword, setAppliedSearchKeyword] = useState("");

  const sortInitialState = {
    quoteId: null,
    customerName: null,
    quoteStatus: null,
    repaymentDueDate: null,
    totalOwing: null
  };
  const { columnOrders, handleSort } = useColumnSort(sortInitialState);

  // Derive the single active sort column/direction for the backend (sortBy + sortOrder)
  const activeSortColumn = Object.keys(columnOrders).find(
    (key) => columnOrders[key]
  );
  const sortBy = activeSortColumn || undefined;
  const sortOrder = activeSortColumn
    ? columnOrders[activeSortColumn]
    : undefined;

  const handleColumnSort = (columnId) => {
    handleSort(columnId);
    setPageIndex(ZERO_NUMBER);
  };

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        startDate: initialStartDate
          ? formatDateWithDayjs(initialStartDate, YYYYMMDD)
          : undefined,
        endDate: initialEndDate
          ? formatDateWithDayjs(initialEndDate, YYYYMMDD)
          : undefined,
        page: pageIndex,
        size: pageSize
      };
      if (appliedSearchKeyword) {
        params.search = appliedSearchKeyword;
      }
      if (sortBy) {
        params.sortBy = sortBy;
        params.sortOrder = sortOrder;
      }

      const response = await privateAxios.get(getTotalOwingNotZeroReportUrl, {
        params
      });
      if (response.data && response.data.status === HTTP_STATUS_SUCCESS) {
        setData(response.data.data);
        setTotalPages(response.data.pageInfo?.totalPages || ZERO_NUMBER);
      } else {
        setData([]);
        setTotalPages(ZERO_NUMBER);
      }
    } catch (error) {
      if (error?.response?.data?.errors?.length > 0) {
        showToast(error.response.data.errors[0], showToastType.error);
      } else {
        showToast(error?.response?.data?.message, showToastType.error);
      }
      setData([]);
      setTotalPages(ZERO_NUMBER);
    } finally {
      setLoading(false);
    }
  }, [
    initialStartDate,
    initialEndDate,
    pageIndex,
    pageSize,
    appliedSearchKeyword,
    sortBy,
    sortOrder
  ]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleFilter = () => {
    setInitialStartDate(selectedStartDate);
    setInitialEndDate(selectedEndDate);
    setAppliedSearchKeyword(searchKeyword);
    setPageIndex(ZERO_NUMBER);
  };

  const handleKeyDown = (e) => {
    if (e.key === ENTER_KEY) {
      handleFilter();
    }
  };

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchKeyword(value);
    if (value === "") {
      setAppliedSearchKeyword("");
      setPageIndex(ZERO_NUMBER);
    }
  };

  const handleDownload = async () => {
    setLoading(true);
    try {
      const params = {
        startDate: initialStartDate
          ? formatDateWithDayjs(initialStartDate, YYYYMMDD)
          : undefined,
        endDate: initialEndDate
          ? formatDateWithDayjs(initialEndDate, YYYYMMDD)
          : undefined
      };
      if (appliedSearchKeyword) {
        params.search = appliedSearchKeyword;
      }
      if (sortBy) {
        params.sortBy = sortBy;
        params.sortOrder = sortOrder;
      }
      const response = await privateAxios.get(
        getTotalOwingNotZeroReportDownloadUrl,
        {
          params,
          responseType: "blob"
        }
      );
      downloadFileFromResponse(response);
    } catch (error) {
      if (error?.response?.data?.errors?.length > 0) {
        showToast(error.response.data.errors[0], showToastType.error);
      } else {
        showToast(error?.response?.data?.message, showToastType.error);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <TablePagesTitle mainTitle="Total owing Report" />
      <ReportPageAction subheading="Total owing Report" />

      <div className="row mb-2 mx-0">
        <div className="col-12 px-0">
          <OwingFilter
            handleStartDateChange={setSelectedStartDate}
            handleEndDateChange={setSelectedEndDate}
            selectedStartDate={selectedStartDate}
            selectedEndDate={selectedEndDate}
            searchKeyword={searchKeyword}
            handleSearchChange={handleSearchChange}
            handleKeyDown={handleKeyDown}
            onFilter={handleFilter}
            onDownload={handleDownload}
            loading={loading}
          />
        </div>
      </div>

      <ErrorBoundary fallback={<ComponentViewFallback />}>
        <div className="row mt-4 mb-2 mx-0 ">
          <div className="col-md-12 px-0">
            <TotalOwingTable
              data={data}
              loading={loading}
              totalPages={totalPages}
              pageIndex={pageIndex}
              pageSize={pageSize}
              setPageIndex={setPageIndex}
              setPageSize={setPageSize}
              columnOrders={columnOrders}
              handleSort={handleColumnSort}
            />
          </div>
        </div>
      </ErrorBoundary>
    </>
  );
}
